import {
	AppointmentItemModel,
	AppointmentsUpdate,
	CalendarInterestedParty,
	CalendarInterests,
	CalendarLiveInformationProvider,
	DateAsNumber
} from "client_legacy/Workspace/LiveInformation/Calendar/provider";
import {
	Dispatch,
	ReactElement,
	SetStateAction,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState
} from "react";
import { TileContext } from "../TileContext";
import { LiveTileContent } from "../LiveTileContent";
import { Guid, newGuid } from "client_legacy/ndaw/guid";
import { localize } from "client_legacy/ndaw/culture";
import { showEmailNotConfiguredDialog } from "client/Features/Email/Dialogs/EmailNotConfiguredDialog";
import { DimensionToRender } from "../../RenderState/common";
import { useUserTimezone, useValueEqualMemo } from "client/Hooks";
import { BusyIndicator } from "client/Components";
import { ArgumentNotSuppliedError, never } from "client_legacy/ndaw/errors";
import { formatDate } from "client_legacy/ndaw/dateFormatting";
import {
	AppointmentSpanFlags,
	AppointmentsViewModelWithSpanFlags,
	calendarLogic
} from "client_legacy/Workspace/LiveInformation/Calendar/calendarLogic";
import { CalendarView } from "./CalendarView/CalendarView";
import { TileRefresh } from "../TileRefresh/TileRefresh";
import { UpcomingEventsView } from "./UpcommingEventsView";
import { OnlineMeeting } from "client/Api/Notifications/notifications";
import { TimeZone } from "client/DateAndTime/timezones";
import { parseDtoDate } from "client/DateAndTime/dateTimeUtils";
import {
	ensureNotNil,
	isNil,
	isNotNil
} from "client/Misc/utils";
import { AppointmentResponseKind } from "client/Api/Calendar/calendar";
import { CalendarDestinationApp, DestinationWindow } from "client/Api/Apps/common";
import { useAddAnalyticsAppEvent } from "client/Features/Analytics/useAddAnalyticsAppEvent";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { SubscriptionTokenCollection } from "client_legacy/ndaw/subscriptionTokenCollection";
import { CalendarEvent, subscribe } from "client/Features/Calendar/events";
import { useComposeAppointmentDialog } from "client/Features/Calendar/ComposeAppointmentDialog/ComposeAppointmentDialog";
import { Action, ActionKind } from "client_legacy/ndaw/actions";
import { LiveTileOverlayState, useLiveTileOverlayState } from "../useLiveTileOverlayState";
import { Coordinate } from "client/Misc/commonTypes";
import { TileStatus } from "../TileStatus";

export type CalendarLiveTileContentProps = {
	provider: CalendarLiveInformationProvider;
	onNavigate(): void;
	destinationApp: CalendarDestinationApp;
	destinationWindow: DestinationWindow;
	onOpenTileMenu: (coordinate: Coordinate) => void;
};

export const CalendarLiveTileContent = (props: CalendarLiveTileContentProps): ReactElement => {
	const {
		provider,
		onNavigate,
		destinationApp,
		destinationWindow,
		onOpenTileMenu
	} = props;

	const [activeTabId, setActiveTabId] = useState<Guid | undefined>(undefined);
	const [calendarLoadingState, setCalendarLoadingState] = useState<ListLoadingState>({
		kind: "loading"
	});

	const [composeAppointmentDialogElement, openComposeAppointmentDialog] = useComposeAppointmentDialog();

	const activeTab = useMemo(
		(): CalendarTab | undefined => {
			if (isNil(activeTabId) || calendarLoadingState.kind !== "success") {
				return undefined;
			}

			return calendarLoadingState.tabs
				.find(x => x.uniqueId === activeTabId);
		},
		[
			activeTabId,
			calendarLoadingState
		]
	);

	const {
		id,
		name,
		dimension,
		icon,
		environmentAppId,
		interactionState,
		appIsLicensed,
		appIsNotAccessible
	} = useContext(TileContext);

	const overlayState: LiveTileOverlayState = useLiveTileOverlayState({
		interactionState,
		dimension,
		appIsLicensed,
		appIsNotAccessible
	});

	useEffect(
		(): (() => void) => {
			const loadCalendarsAsync = async (signal: AbortSignal): Promise<void> => {
				setCalendarLoadingState({ kind: "loading" });

				try {
					const [defaultCalendarId, calendars] = await Promise.all([
						provider.getPreference(id, signal),
						provider.listCalendars(signal)
					]);

					const tabs: readonly CalendarTab[] = calendars.map(
						(x): CalendarTab => ({
							uniqueId: newGuid(),
							calendarId: x.id,
							name: x.name
						})
					);

					setCalendarLoadingState({
						kind: "success",
						tabs
					});

					let tabToShow: CalendarTab | undefined = isNotNil(defaultCalendarId)
						? tabs.find((x) => x.calendarId === defaultCalendarId)
						: undefined;

					tabToShow ??= tabs[0];
					if (tabToShow) {
						setActiveTabId(tabToShow.uniqueId);
					}
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);
					setCalendarLoadingState({ kind: "error" });
				}
			};

			const cancellation = new AbortController();
			loadCalendarsAsync(cancellation.signal);
			return (): void => cancellation.abort();
		},
		[id, provider]
	);

	const userTimezone: TimeZone = useUserTimezone();

	const {
		addEvent: addAnalyticsEvent
	} = useAddAnalyticsAppEvent(environmentAppId);

	const createNewHandler = useCallback(
		async (): Promise<void> => {
			addAnalyticsEvent();
			if (!provider.isExchangeAvailable) {
				showEmailNotConfiguredDialog(false, !provider.isExchangeEnabled);
				return;
			}

			const result: TryResult<readonly string[]> = await openComposeAppointmentDialog({
				timezone: userTimezone
			});

			if (result.wasSuccessful) {
				provider.forceReload();
			}
		},

		[
			provider,
			userTimezone,
			addAnalyticsEvent,
			openComposeAppointmentDialog
		]
	);

	const navigateHandler = useCallback(
		(): void => {
			addAnalyticsEvent();
			onNavigate();
		},
		[
			onNavigate,
			addAnalyticsEvent
		]
	);

	const changeTabHandler = useCallback(
		(tabId: Guid): void => {
			setActiveTabId(tabId);

			const tabs: readonly CalendarTab[] = calendarLoadingState.kind === "success"
				? calendarLoadingState.tabs
				: [];

			const tab: CalendarTab = ensureNotNil(
				tabs.find((x) => x.uniqueId === tabId)
			);

			provider.setPreference(id, tab.calendarId)
				.catch((e) => console.error(`Failed to set the calendar tile preference (${id})`, e));
		},
		[
			calendarLoadingState,
			id,
			provider
		]
	);

	const defaultActions = useMemo(
		(): readonly Action<void>[] => listDefaultActions({
			new: createNewHandler
		}),
		[createNewHandler]
	);

	const renderContent = (): ReactElement | null => {
		switch (calendarLoadingState.kind) {
			case "loading":
				return <BusyIndicator />;
			case "error":
				return (
					<LiveTileContent.Message text={localize("_UnableToRetrieveItems")} />
				);
			case "success":
				// continue down below
				break;
			default:
				never(calendarLoadingState);
		}

		if (!activeTab) {
			return null;
		}

		return (
			<Tab
				key={activeTab.uniqueId}
				data={activeTab}
				dimension={dimension}
				provider={provider}
				timezone={userTimezone}
				destinationApp={destinationApp}
				destinationWindow={destinationWindow}
				addAnalyticsEvent={addAnalyticsEvent}
			/>
		);
	};

	return (
		<>
			<LiveTileContent
				icon={icon}
				name={name}
				overlay={overlayState}
				onOpenTileMenu={onOpenTileMenu}
				overlayStatusIcon={<TileStatus />}
			>
				<LiveTileContent.Header
					icon={icon}
					actions={defaultActions}
					actionContext={undefined}
				>
					<LiveTileContent.TabSelector
						tileId={id}
						tileName={name}
						value={activeTabId}
						onChange={changeTabHandler}
						tabs={calendarLoadingState.kind === "success"
							? calendarLoadingState.tabs
							: []
						}
					/>
				</LiveTileContent.Header>

				<LiveTileContent.Content>
					{renderContent()}
				</LiveTileContent.Content>

				<LiveTileContent.Footer
					name={localize("_Calendar")}
					onClick={navigateHandler}
				/>
			</LiveTileContent>
			{composeAppointmentDialogElement}
		</>
	);
};
CalendarLiveTileContent.displayName = "CalendarLiveTileContent";

type ActionHandlers = {
	new: () => void;
};

function listDefaultActions(
	handlers: ActionHandlers
): readonly Action<void>[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "new-appointment",
			title: localize("_NewCalendarEvent"),
			icon: "plus",
			canExecute: () => true,
			execute: () => handlers.new()
		}
	];
}

type CalendarTab = {
	uniqueId: Guid;
	name: string;
	calendarId: string;
};

type ListLoadingState =
	| { kind: "loading"; }
	| { kind: "error"; }
	| { kind: "success"; tabs: readonly CalendarTab[]; };

type TabProps = {
	data: CalendarTab;
	dimension: DimensionToRender;
	provider: CalendarLiveInformationProvider;
	timezone: TimeZone;
	destinationApp: CalendarDestinationApp;
	destinationWindow: DestinationWindow;
	addAnalyticsEvent(): void;
};

const Tab = (props: TabProps): ReactElement => {
	const {
		data,
		dimension,
		provider,
		timezone,
		destinationApp,
		destinationWindow,
		addAnalyticsEvent
	} = props;

	const [appointmentsPerDates, setAppointmentsPerDates] = useState<Map<DateAsNumber, readonly AppointmentItemModel[]>>(new Map());
	const [upcomingAppointments, setUpcomingAppointments] = useState<readonly AppointmentItemModel[]>([]);

	const [dates, setDates] = useState<readonly DateAsNumber[]>([]);

	const [loadingState, setLoadingState] = useState<LoadingState>({
		isLoading: true
	});

	useCalendarEvents(setAppointmentsPerDates);

	const isCalendarViewAvailable: boolean = dimension.kind === "normal"
		&& dimension.dimension
			.columnSpan > 4;

	const party = useMemo(
		(): CalendarInterestedParty => {
			return {
				partyId: data.uniqueId,
				resourceId: data.calendarId,
				informAboutGlobalRetrievalFailure: () => {
					setLoadingState({
						isLoading: false,
						hasGlobalFailure: true
					});
				},
				informAboutConsecutiveErrors: () => {
					setLoadingState({
						isLoading: false,
						hasTooManyConsecutiveErrors: true
					});
				},
				update: (loadResult: AppointmentsUpdate): void => {
					if (!loadResult) {
						throw new ArgumentNotSuppliedError("appointmentsLoadResult");
					}

					if (loadResult.isError === true) {
						const {
							errorMessage
						} = loadResult;

						setLoadingState({
							isLoading: false,
							appointmentsLoadErrorMessage: errorMessage
						});

						return;
					}

					setLoadingState({
						isLoading: false
					});

					if (loadResult.appointmentsByDates) {
						setAppointmentsPerDates(
							new Map<number, readonly AppointmentItemModel[]>(loadResult.appointmentsByDates)
						);
					}

					if (loadResult.upcomingAppointments) {
						setUpcomingAppointments(loadResult.upcomingAppointments);
					}
				}
			};
		},
		[data.calendarId, data.uniqueId]
	);

	const interests = useValueEqualMemo(
		(): CalendarInterests => {
			const result: CalendarInterests = {};

			if (isCalendarViewAvailable) {
				result.dates = dates;
			} else {
				result.upcoming = true;
			}

			return result;
		},
		[
			dates,
			isCalendarViewAvailable
		]
	);

	useEffect(
		(): (() => void) => {
			provider.register(party, interests);

			return (): void => {
				provider.unregister(party);
			};
		},
		[
			interests,
			party,
			provider
		]
	);

	const renderContent = (): ReactElement => {
		if (!loadingState.isLoading) {
			if (loadingState.hasTooManyConsecutiveErrors) {
				return (
					<TileRefresh onRefresh={() => provider.forceReload()} />
				);
			}

			if (loadingState.hasGlobalFailure) {
				return (
					<LiveTileContent.NoResults
						icon="gear"
						title={localize("_UnableToRetrieveItems")}
						message={localize("_WeWereUnableToRetrieveTheItems")}
						onRetry={() => provider.forceReload()}
						trackingId="has-error"
					/>
				);
			}
		}

		const appointmentsLoadErrorMessage: string | undefined = !loadingState.isLoading
			? loadingState.appointmentsLoadErrorMessage
			: undefined;

		return isCalendarViewAvailable
			? (
				<CalendarView
					appointmentsPerDates={appointmentsPerDates}
					errorMessage={appointmentsLoadErrorMessage}
					onDatesChanged={setDates}
					shouldShowLoadingIndicator={!loadingState.isLoading && isNil(appointmentsLoadErrorMessage)}
					timezone={timezone}
					destinationApp={destinationApp}
					destinationWindow={destinationWindow}
					onListItemClick={addAnalyticsEvent}
					calendarId={data.calendarId}
				/>
			) : (
				<UpcomingEventsView
					upcomingAppointments={upcomingAppointments}
					errorMessage={appointmentsLoadErrorMessage}
					timezone={timezone}
					destinationApp={destinationApp}
					destinationWindow={destinationWindow}
					onListItemClick={addAnalyticsEvent}
					calendarId={data.calendarId}
				/>
			);

	};

	return (
		<LiveTileContent.ContentStack>
			<LiveTileContent.BusyIndicatorContainer>
				{loadingState.isLoading && (
					<BusyIndicator />
				)}
				{renderContent()}
			</LiveTileContent.BusyIndicatorContainer>
		</LiveTileContent.ContentStack>
	);
};
Tab.displayName = "Tab";

type LoadingState = {
	isLoading: true;
} | {
	isLoading: false;
} & (
	{
		hasTooManyConsecutiveErrors: true;
		hasGlobalFailure?: undefined;
		appointmentsLoadErrorMessage?: undefined;
	}
	|
	{
		hasGlobalFailure: true;
		hasTooManyConsecutiveErrors?: undefined;
		appointmentsLoadErrorMessage?: undefined;
	}
	|
	{
		appointmentsLoadErrorMessage: string;
		hasTooManyConsecutiveErrors?: undefined;
		hasGlobalFailure?: undefined;
	}
	|
	{
		hasTooManyConsecutiveErrors?: undefined;
		hasGlobalFailure?: undefined;
		appointmentsLoadErrorMessage?: undefined;
	}
);

export type DateToDisplay = {
	uniqueKey: string;
	date: {
		isToday: boolean;
		value: Date;
	};
	appointments: readonly Appointment[];
};

export type Appointment = {
	subject: string;
	id: string | null;
	uniqueId: string | Guid;
	locations: readonly string[] | null;
	startTime: string;
	finishTime: string | undefined;
	meeting: OnlineMeeting | null;
	start: Date | null;
	end: Date | null;
};

export function getAppointmentsForDay(
	allAppointments: readonly AppointmentItemModel[],
	day: Date,
	timezone: TimeZone
): Appointment[] {
	const appointmentsForDay: (AppointmentItemModel & AppointmentSpanFlags)[] = calendarLogic
		.getAppointmentsPerDay(
			allAppointments,
			day,
			timezone
		);

	return appointmentsForDay.map(appointment => {
		const {
			id,
			uniqueId,
			locations,
			subject,
			meeting
		} = appointment;

		return {
			subject: subject
				? subject
				: localize("_Busy"),
			id,
			uniqueId,
			locations,
			startTime: formatStartTime(appointment, timezone),
			finishTime: formatFinishTime(appointment, timezone),
			meeting,
			start: parseDtoDate(appointment.start),
			end: parseDtoDate(appointment.end),
		};
	});
}

const STARTS_BEFORE_DAY_PREFIX = "<<";
const ENDS_AFTER_DAY_SUFFIX = ">>";

function formatStartTime(
	appointment: AppointmentsViewModelWithSpanFlags,
	timezone: TimeZone
): string {
	const isAllDayEvent: boolean = appointment.isAllDayEvent ||
		(appointment.startsBeforeDay && appointment.endsAfterDay);

	if (isAllDayEvent) {
		return localize("_AllDay");
	}

	if (appointment.startsBeforeDay) {
		return STARTS_BEFORE_DAY_PREFIX;
	}

	return formatDate(
		appointment.start,
		"T",
		timezone
	);
}

function formatFinishTime(
	appointment: AppointmentsViewModelWithSpanFlags,
	timezone: TimeZone
): string | undefined {
	const isAllDayEvent: boolean = appointment.isAllDayEvent
		|| (appointment.startsBeforeDay && appointment.endsAfterDay);

	if (isAllDayEvent && (!appointment.startsBeforeDay && !appointment.endsAfterDay)) {
		return undefined;
	}

	if (appointment.startsBeforeDay && appointment.endsAfterDay) {
		return `${STARTS_BEFORE_DAY_PREFIX} ${ENDS_AFTER_DAY_SUFFIX}`;
	}

	if (appointment.endsAfterDay) {
		return ENDS_AFTER_DAY_SUFFIX;
	}

	return formatDate(
		appointment.end,
		"T",
		timezone
	);
}

export function useCalendarEvents(
	setAppointmentsPerDates: Dispatch<SetStateAction<Map<DateAsNumber, readonly AppointmentItemModel[]>>>
): void {
	useEffect(
		(): (() => void) => {
			const subscriptionTokens: SubscriptionTokenCollection = registerSubscriptions(setAppointmentsPerDates);

			return (): void => subscriptionTokens.destroy();
		},
		[setAppointmentsPerDates]
	);
}

function registerSubscriptions(
	setAppointmentsPerDates: Dispatch<SetStateAction<Map<DateAsNumber, readonly AppointmentItemModel[]>>>
): SubscriptionTokenCollection {
	const result = new SubscriptionTokenCollection();

	const respondedToken: PubSubSubscriptionToken = subscribe(
		CalendarEvent.Responded,
		(_, { id, response }): void => {
			if (response !== AppointmentResponseKind.Decline) {
				return;
			}

			const isAffected: (appointment: AppointmentItemModel) => boolean = "master" in id
				? (x) => x.masterId === id.master
				: (x) => x.id === id.appointment;

			setAppointmentsPerDates(
				(appointmentsPerDates) => {
					const newMap: Map<number, readonly AppointmentItemModel[]> = new Map(appointmentsPerDates);

					for (const entry of newMap) {
						const [
							date,
							appointments
						] = entry;

						const filteredAppointments: AppointmentItemModel[] = appointments.filter((x) => !isAffected(x));

						if (filteredAppointments.length !== appointments.length) {
							newMap.set(date, filteredAppointments);
						}
					}

					return newMap;
				}
			);
		}
	);

	result.add(respondedToken);

	return result;
}
